import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { notes } from '../data/notes';

export default function Notes() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const seriesNames = Array.from(new Set(notes.map(n => n.series)));

  return (
    <div style={{ paddingTop: 64 }}>
      <section style={{ padding: '72px 24px 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: -80, right: '10%',
          width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,229,200,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="badge" style={{ marginBottom: 20 }}>
            <BookOpen size={11} /> Learning in public
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)',
            fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: 16,
          }}>
            Tech notes,<br />
            <span style={{ color: 'var(--cyan)' }}>from basics to depth</span>
          </h1>
          <p style={{ color: 'var(--text2)', fontSize: 18, maxWidth: 560 }}>
            Small, focused chunks — one topic per note, never several crammed together. Growing as I learn.
          </p>
        </div>
      </section>

      <section style={{ padding: '0 24px 100px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {seriesNames.map(series => {
            const seriesNotes = notes.filter(n => n.series === series).sort((a, b) => a.order - b.order);
            return (
              <div key={series} style={{ marginBottom: 48 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 20 }}>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22 }}>{series}</h2>
                  <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>{seriesNotes.length} note{seriesNotes.length > 1 ? 's' : ''}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
                  {seriesNotes.map(note => (
                    <Link to={`/notes/${note.slug}`} key={note.slug} style={{
                      display: 'flex', flexDirection: 'column', gap: 16,
                      padding: '28px', borderRadius: 'var(--radius)',
                      background: 'var(--bg2)', border: '1px solid var(--border)',
                      transition: 'all 0.3s',
                    }}
                    onMouseOver={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = `${note.color}40`;
                      (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
                      (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <span style={{
                          fontFamily: 'var(--font-mono)', fontSize: 11, padding: '3px 10px', borderRadius: 100,
                          color: note.color, background: `${note.color}18`, border: `1px solid ${note.color}30`,
                        }}>Part {note.order}</span>
                        <ArrowRight size={18} style={{ color: 'var(--text3)', flexShrink: 0 }} />
                      </div>
                      <div>
                        <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>{note.title}</h3>
                        <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6 }}>{note.summary}</p>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 'auto' }}>
                        {note.tags.map(t => <span key={t} className="tag">{t}</span>)}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
